import React, { useState, useEffect, useMemo } from 'react';
import { useRef } from 'react';

import { useLocation } from 'react-router-dom';
import useApiControl from './common/useApiControl';
import {SSEProvider} from './common/useSseControl';
import LoginPage from './login.js';
import OwnerRoom from './OwnerRoom';
import MemberRoom from './MemberRoom';
import CreateRoom from './CreateRoom';
import BaseHeader from './components/headers/BaseHeader';
import JoinEventDialog from './components/dialogs/JoinEventDialog';
import QuickJoinInvitePage from './components/invite/QuickJoinInvitePage';
import { markQuickJoinNameSetupRequired } from './common/quickJoinNameSetup';
import { isTopJoinConfirmed } from './common/topJoinConfirmation';

import Box from '@mui/material/Box';


function Room(props) {
    console.log(`[${new Date().toISOString()}] room rendered`);
    const sse_base = "/api/sse_updates";

    const search = useLocation().search;
    const query = new URLSearchParams(search);
    const room_id = query.get('id');
    
    const [event, setEvent] = useState(null);
    const [round, setRound] = useState(null);
    const [user, setUser] = useState(null);
    const [roundSummary, setRoundSummary] = useState(null);
    const [roomRefreshKey, setRoomRefreshKey] = useState(0);

    const [requiresLogin, setRequiresLogin] = useState(false);
    const [invalidRoomRequested, setInvalidRoomRequested] = useState(false);
    const [exceptionOccurred3, setExceptionOccurred3] = useState(false);
    const [sseUrl, setSseUrl] = useState('');
    const [nextComponent, setNextComponent] = useState(props.page);
    const [entryConfirmed, setEntryConfirmed] = useState(() => isTopJoinConfirmed(room_id));
    const [isJoinDialogVisible, setIsJoinDialogVisible] = useState(false);
    const [quickJoinEvent, setQuickJoinEvent] = useState(null);
    const [isQuickJoinDialogVisible, setIsQuickJoinDialogVisible] = useState(false);
    const [quickJoinMessage, setQuickJoinMessage] = useState('');
    const [quickJoinNameMessage, setQuickJoinNameMessage] = useState('');
    const afterLogin = props.page;

    console.log('room' + room_id);
    
    const params1 = useMemo(() => ({
        room_id: room_id,
    }), [room_id]);

    const previewParams = useMemo(() => ({
        room_id: room_id,
        limit: 1,
    }), [room_id]);
    
    //event
    const { data: data1, loading: loading1, executeRequest: executeRequest1 } = useApiControl({
        action: '/get_event_context',
        params: params1,
        method: 'POST',
    });

    const { executeRequest: executePreviewRequest } = useApiControl({
        action: '/get_events',
        params: previewParams,
        method: 'POST',
    });

    const { loading: quickJoining, executeRequest: executeQuickJoinRequest } = useApiControl({
        action: '/quick_join_event',
        params: params1,
        method: 'POST',
    });

    //event
    useEffect(() => {
        if (room_id) {
            executeRequest1().catch((error) => {
                console.error(error);
                if (error.response && error.response.status === 401) {
                    setRequiresLogin(true);
                    executePreviewRequest().then((response) => {
                        const previewEvent = response?.events?.[0] ?? null;
                        if (!previewEvent) {
                            setInvalidRoomRequested(true);
                            return;
                        }
                        setQuickJoinEvent(previewEvent);
                        setIsQuickJoinDialogVisible(true);
                    }).catch((previewError) => {
                        console.error(previewError);
                        setInvalidRoomRequested(true);
                    });
                    setInvalidRoomRequested(false);
                } else {
                    setRequiresLogin(false);
                    setInvalidRoomRequested(true);
                }

                // setNextComponent('login');
            });
        }
    }, [room_id]);

    useEffect(() => {
        if (data1) {
            setRequiresLogin(false);
            setInvalidRoomRequested(false);
            setEvent(data1.event);
            setRound(data1.round);
            setUser(data1.user);
            setRoundSummary(data1.round_summary ?? null);
            localStorage.setItem('event', JSON.stringify(data1.event));
            if (data1.owner) {
                setNextComponent(props.page === 'owner' ? 'owner' : 'ownerConsole');
            } else if (data1.user) {
                setNextComponent('member');
            } else if (!entryConfirmed && props.page === 'member') {
                setNextComponent('entry');
                setIsJoinDialogVisible(true);
            } else {
                setNextComponent('member');
            }
        }
    }, [data1, entryConfirmed, props.page]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (
                document.visibilityState !== 'visible' ||
                !['member', 'owner'].includes(nextComponent) ||
                !room_id
            ) {
                return;
            }

            executeRequest1().then((response) => {
                if (!response?.event || !response?.round || !response?.user) {
                    return;
                }

                setRequiresLogin(false);
                setInvalidRoomRequested(false);
                setEvent(response.event);
                setRound(response.round);
                setUser(response.user);
                setRoundSummary(response.round_summary ?? null);
                localStorage.setItem('event', JSON.stringify(response.event));
                setRoomRefreshKey((current) => current + 1);
            }).catch((error) => {
                console.error('ルームの画面復帰時再同期に失敗しました。', error);
            });
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [executeRequest1, nextComponent, room_id]);

    const updateRoom = () => {
        executeRequest1();
    }

    const getQuickJoinNameErrorMessage = (error) => {
        if (error.response?.status !== 422) {
            return '';
        }

        const nameErrors = error.response?.data?.errors?.name;
        const firstNameError = Array.isArray(nameErrors) ? nameErrors[0] : nameErrors;
        if (!firstNameError) {
            return '';
        }

        const message = String(firstNameError);
        const isUniqueError = (
            message.includes('taken') ||
            message.includes('unique') ||
            message.includes('既に') ||
            message.includes('すでに')
        );

        return isUniqueError ? message : '';
    };

    const handleQuickJoin = (name = null) => {
        setQuickJoinMessage('');
        setQuickJoinNameMessage('');

        const hasName = typeof name === 'string' && name.trim() !== '';
        const requestParams = hasName
            ? { ...params1, name: name.trim() }
            : params1;

        executeQuickJoinRequest(requestParams).then((response) => {
            const { user: registeredUser, token } = response;
            localStorage.setItem('authToken', token);
            localStorage.setItem('user', JSON.stringify(registeredUser));
            if (response?.requires_name_setup) {
                markQuickJoinNameSetupRequired(registeredUser, room_id);
            }
            setIsQuickJoinDialogVisible(false);
            setRequiresLogin(false);
            setEntryConfirmed(true);
            executeRequest1();
        }).catch((error) => {
            console.error(error);
            const nameErrorMessage = hasName ? getQuickJoinNameErrorMessage(error) : '';
            if (nameErrorMessage) {
                setQuickJoinNameMessage(nameErrorMessage);
                return;
            }

            setQuickJoinMessage(error.response?.data?.message || 'ルームへの参加に失敗しました。');
        });
    };

    const handleJoinConfirm = () => {
        setEntryConfirmed(true);
        setIsJoinDialogVisible(false);
        setNextComponent('member');
    };

    //set SSE URL
    useEffect(() => {
        if (event?.id) {
            setSseUrl(sse_base + "?index=" + event.id);
        }
    }, [event]);

    useEffect(() => {
        // todo
        // リクエストされたページに行けず、react内でリダイレクトされるときここで一声かけてあげる

    }, [nextComponent]);

    // id指定なしTopへ
    if ((room_id == null && nextComponent === 'member')) {
        window.location.href = './';
    }

    // id指定のミス
    if (invalidRoomRequested) {
        alert('指定のページは有効ではありません。');
        window.location.href = './';
    }

    if (isQuickJoinDialogVisible) {
        return (
            <>
                <BaseHeader />
                <QuickJoinInvitePage
                    event={quickJoinEvent}
                    loading={quickJoining}
                    message={quickJoinMessage}
                    nameMessage={quickJoinNameMessage}
                    onJoin={handleQuickJoin}
                    onLogin={() => window.location.href = '/login'}
                />
            </>
        );
    }

    if (isJoinDialogVisible) {
        return (
            <>
                <BaseHeader />
                <JoinEventDialog
                    isVisible={isJoinDialogVisible}
                    onClose={() => setIsJoinDialogVisible(false)}
                    event={event}
                    user={{ ticket: undefined }}
                    onJoin={handleJoinConfirm}
                />
            </>
        );
    }

    // sseUrlが設定できていれば
    if (sseUrl) {
        if (nextComponent === 'ownerConsole') {
            window.location.href = `/owner-console/${room_id}`;
            return null;
        }

        if (nextComponent === 'owner') {
            return (
                <SSEProvider url={sseUrl}>
                    <Box sx={{ width: "100%" }}>
                        {/* <div>ownerの場合</div> */}
                        <OwnerRoom
                            key={roomRefreshKey}
                            event={event}
                            round={round}
                            user={user}
                            room_id={room_id}
                            updateRoom={updateRoom}>    
                        </OwnerRoom>
                    </Box>
                </SSEProvider>
            );
        } else if(nextComponent === 'member') {
            return (
                <SSEProvider url={sseUrl}>
                    <Box sx={{ width: "100%" }}>
                        {/* <div>memberの場合</div> */}
                        <MemberRoom
                            key={roomRefreshKey}
                            event={event}
                            round={round}
                            user={user}
                            room_id={room_id}
                            initialRoundSummary={roundSummary}
                        />
                    </Box>
                </SSEProvider>
            );
        }
    } else if(room_id == null && nextComponent === 'owner') {
        // idのないownerリクエストは新規作成（user取得済みのみ表示）
        return(
            <CreateRoom/>
        )
    // 非ログイン状態を検知したらログインページへ
    } else if(requiresLogin || nextComponent === 'login' || (room_id == null && nextComponent === 'owner' && !user)) {
        return(        
            <LoginPage afterLogin={afterLogin} room_id={room_id} />
        )
    }

}

export default Room;
